import React from "react";
import {
  FaUtensils,
  FaTruck,
  FaBox,
  FaIndustry,
  FaCheck,
  FaArrowRight,
  FaWineGlassAlt,
  FaConciergeBell,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

import heroBg from "../assets/food&beverages.jpg"; 
import ChallengesImage from "../assets/food.webp";

const FoodBeveragesIndustry = () => {

  // ⭐ SERVICES
  const services = [
    {
      icon: FaUtensils,
      title: "Restaurant & Café Management",
      description:
        "End-to-end digital solutions for dine-in, takeaway, reservations, and customer engagement.",
      features: [
        "Smart Menu Management",
        "Table Reservations",
        "POS Integration",
        "Customer Loyalty Programs",
      ],
      color: "from-yellow-500 to-orange-500",
      iconColor: "text-white",
    },
    {
      icon: FaTruck,
      title: "Cloud Kitchen & Food Delivery",
      description:
        "Optimized systems that improve delivery speed, reduce operational cost, and manage multi-brand kitchens.",
      features: [
        "Delivery Optimization",
        "Inventory Tracking",
        "Order Automation",
        "Multi-brand Management",
      ],
      color: "from-red-500 to-rose-500",
      iconColor: "text-white",
    },
    {
      icon: FaIndustry,
      title: "Food Processing & Packaging",
      description:
        "Digital management for large-scale food production units and automated packaging workflows.",
      features: [
        "Quality Control Systems",
        "Batch Tracking",
        "Equipment Monitoring",
        "Automated Packaging Line Support",
      ],
      color: "from-blue-500 to-cyan-500",
      iconColor: "text-white",
    },
    {
      icon: FaBox,
      title: "Supply Chain & Distribution",
      description:
        "Streamlined distribution for restaurants, cloud kitchens, and processing units.",
      features: [
        "Cold Storage Tracking",
        "Logistics Planning",
        "Distributor Management",
        "Demand Forecasting",
      ],
      color: "from-purple-500 to-fuchsia-500",
      iconColor: "text-white",
    },
    {
      icon: FaConciergeBell,
      title: "Customer Experience Solutions",
      description:
        "Tools that enhance dining experiences and increase customer repeat rate.",
      features: [
        "Feedback Systems",
        "AI Chat Support",
        "Personalized Recommendations",
        "Customer Profiling",
      ],
      color: "from-emerald-500 to-green-500",
      iconColor: "text-white",
    },
    {
      icon: FaWineGlassAlt,
      title: "Branding & Digital Reach",
      description:
        "Grow your food brand across social media, search engines, and local discovery platforms.",
      features: [
        "Food Branding",
        "SEO & SEM",
        "Food Photography",
        "Influencer Marketing",
      ],
      color: "from-indigo-500 to-violet-500",
      iconColor: "text-white",
    },
  ];

  // ⭐ SEGMENTS
  const segments = [
    {
      category: "Restaurants & Cafés",
      items: [
        "Fine Dining Restaurants",
        "Cafés & Coffee Shops",
        "Bakeries & Desserts",
        "Quick Service Restaurants",
        "Buffet & Multi-cuisine",
        "Bars & Lounges",
        "Family Restaurants",
        "Theme-based Restaurants",
      ],
      icon: FaUtensils,
      iconColor: "text-white",
    },
    {
      category: "Cloud Kitchens & Delivery",
      items: [
        "Multi-brand Kitchens",
        "Fast Food Delivery Brands",
        "Meal Subscription Kitchens",
        "Regional Cuisine Kitchens",
        "Dark Stores",
        "Last-mile Delivery",
        "Shared Kitchen Spaces",
        "Health & Diet Food Kitchens",
      ],
      icon: FaTruck,
      iconColor: "text-white",
    },
    {
      category: "Food Processing & Packaging",
      items: [
        "Packaged Food Manufacturers",
        "Frozen Food Units",
        "Ready-to-eat Brands",
        "Dairy & Beverage Units",
        "Spices & Sauces Manufacturers",
        "Automated Packaging Plants",
        "Quality Inspection Units",
        "Cold Chain Facilities",
      ],
      icon: FaBox,
      iconColor: "text-white",
    },
  ];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="min-h-screen">

        {/* HERO SECTION */}
        <div
          className="relative bg-cover bg-center bg-no-repeat text-white py-24 overflow-hidden"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="absolute inset-0 bg-black/60"></div>

          <div className="container mx-auto px-6 relative z-10 text-center">
            <h1 className="text-6xl font-bold mb-6">
              Food & Beverages Industry
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Smart solutions for restaurants, cloud kitchens, and food processing units.
            </p>
          </div>
        </div>

        {/* SERVICES */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold mb-6">Food & Beverage Services</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
                Advanced solutions for the entire food ecosystem.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 hover:border-yellow-500/50 hover:-translate-y-2 transition-all"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-3 rounded-2xl shadow-lg">
                      <service.icon className={`text-2xl ${service.iconColor}`} />
                    </div>
                    <h3 className="text-2xl font-bold">{service.title}</h3>
                  </div>

                  <p className="text-gray-300 mb-6">{service.description}</p>

                  <div className="space-y-3">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-center">
                        <div className="bg-yellow-300/20 p-1 rounded-lg mr-3">
                          <FaCheck className="text-yellow-400 text-sm" />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* CHALLENGES */}
        <div className="pt-10 pb-20 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-5xl font-bold text-center mb-16">
              Challenges in the Food & Beverage Industry
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                <div className="bg-white p-6 border rounded-2xl shadow-xl">
                  <h3 className="text-4xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent mb-4">
                    01
                  </h3>
                  <h4 className="font-bold mb-2">High Operational Costs</h4>
                  <p className="text-gray-600">
                    Optimize kitchen workflow, reduce waste, and automate repetitive processes.
                  </p>
                </div>

                <div className="bg-white p-6 border rounded-2xl shadow-xl">
                  <h3 className="text-4xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent mb-4">
                    02
                  </h3>
                  <h4 className="font-bold mb-2">Inventory & Food Wastage</h4>
                  <p className="text-gray-600">
                    Track ingredients, forecast demand, and reduce spoilage.
                  </p>
                </div>

                <div className="bg-white p-6 border rounded-2xl shadow-xl">
                  <h3 className="text-4xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent mb-4">
                    03
                  </h3>
                  <h4 className="font-bold mb-2">Maintaining Consistent Quality</h4>
                  <p className="text-gray-600">
                    Use automated quality checks and standardized recipe procedures.
                  </p>
                </div>

                <div className="bg-white p-6 border rounded-2xl shadow-xl">
                  <h3 className="text-4xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent mb-4">
                    04
                  </h3>
                  <h4 className="font-bold mb-2">Competition & Customer Retention</h4>
                  <p className="text-gray-600">
                    Improve digital presence, add loyalty programs, and personalize experiences.
                  </p>
                </div>

              </div>

              <div className="flex justify-center">
                <img
                  src={ChallengesImage}
                  className="rounded-3xl shadow-2xl w-full max-w-xl"
                  alt=""
                />
              </div>

            </div>
          </div>
        </div>
        <div className="pt-10 pb-20 bg-white">
                  <div className="container mx-auto px-6">
        
                    {/* Heading */}
                    <h2 className="text-5xl font-bold text-gray-900 text-center mb-16">
                      How <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent underline decoration-blue-400 decoration-4">Amplinova</span> turns that challenges into opportunity
                    </h2>
        
                    {/* 4 Items — Side by side */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        
                      {/* 01 */}
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                          <FaCheck className="text-white text-3xl" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          Personalized Travel Experiences
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          Turn complex planning into curated, tailor-made tours that offer a unique and memorable travel experience.
                        </p>
                      </div>
        
                      {/* 02 */}
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                          <FaCheck className="text-white text-3xl" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          Stronger Digital Reach
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          Leverage social media, SEO, and content marketing to attract global travelers and boost brand visibility.
                        </p>
                      </div>
        
                      {/* 03 */}
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                          <FaCheck className="text-white text-3xl" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                         Loyalty & Repeat Customers
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          Create loyalty programs, referral rewards, and exclusive
                           offers to turn one-time travelers into repeat clients.
                        </p>
                      </div>
        
                      {/* 04 */}
                      <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                          <FaCheck className="text-white text-3xl" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          Niche Travel Markets
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          Expand into niche segments like adventure tours, 
                          luxury travel, pilgrimage trips, and wellness 
                          tourism for higher margins.
                        </p>
                      </div>
        
                    </div>
                  </div>
                </div>

        {/* SEGMENTS */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="container mx-auto px-6">
            <h2 className="text-5xl font-bold text-center mb-16">
              Food & Beverage Segments We Serve
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg border hover:shadow-xl transition-all"
                >
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-4 rounded-2xl inline-flex mb-6">
                    <segment.icon className={`text-2xl ${segment.iconColor}`} />
                  </div>

                  <h3 className="text-2xl font-bold mb-6">{segment.category}</h3>

                  <div className="space-y-3">
                    {segment.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center text-gray-700">
                        <div className="bg-green-100 p-1 rounded-lg mr-3">
                          <FaCheck className="text-green-600 text-sm" />
                        </div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* CTA */}
        <div className="relative pt-10 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-40"></div>

          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-5xl font-bold text-white mb-8">
              Ready to Transform Your Food Business?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
              From restaurants to large-scale food units — we provide powerful digital solutions
              to boost your brand and streamline operations.
            </p>

            <button
              onClick={() => (window.location.href = "/contact-us")}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-12 py-5 rounded-xl font-semibold hover:scale-105 transition flex items-center gap-3 mx-auto"
            >
              Grow Your Food Business
              <FaArrowRight />
            </button>
          </div>
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default FoodBeveragesIndustry;
